"use client"

import React, { useState, useRef, useMemo } from 'react';
import { ScrollArea } from './scroll-area';
import { Button } from './button';
import { toast } from '@/components/ui';
import { ChevronDown, ChevronRight, Copy, Check, Edit3, X, Download, Maximize } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { CodeEditor } from './code-editor';


interface JsonViewerProps {
  data: any;
  className?: string;
  theme?: 'default' | 'aurora';
  defaultExpanded?: boolean;
  maxHeight?: string;
  showCopy?: boolean;
  showDownload?: boolean;
  editable?: boolean;
  onDataChange?: (newData: any) => void;
  fileName?: string;
}

interface JsonNodeProps {
  data: any;
  keyName?: string;
  level?: number;
  isLast?: boolean;
  theme?: 'default' | 'aurora';
  defaultExpanded?: boolean;
}

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  theme?: 'default' | 'aurora';
  className?: string;
  placeholder?: string;
  maxHeight?: string;
}

/**
 * JSON Editor Component with Syntax Highlighting
 * 
 * A code editor component that provides JSON syntax highlighting while editing.
 */
function JsonEditor({ 
  value, 
  onChange, 
  theme = 'default',
  className = '',
  placeholder = 'Enter valid JSON...',
  maxHeight = '800px'
}: JsonEditorProps) {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode
  React.useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);



  return (
    <div className={cn('w-full', className)} style={{ maxHeight }}>
      <div className="border border-border rounded-md overflow-hidden">
        <CodeEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          language="json"
          theme={isDarkMode ? 'dark' : 'light'}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            dropCursor: false,
            allowMultipleSelections: false,
            searchKeymap: true,
          }}
          style={{
            fontSize: '14px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          }}
          height={maxHeight}
          minHeight="400px"
        />
      </div>
    </div>
  );
}

/**
 * JSON Viewer Component
 * 
 * A beautiful, interactive JSON viewer with tree navigation, syntax highlighting,
 * and collapsible nodes. Integrates with the aurora theme system.
 */
export function JsonViewer({ 
  data, 
  className = '', 
  theme = 'default',
  defaultExpanded = false,
  maxHeight = '800px',
  showCopy = true,
  showDownload = false,
  editable = false,
  onDataChange,
  fileName = 'data.json'
}: JsonViewerProps) {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJsonString, setEditedJsonString] = useState('');
  const [parseError, setParseError] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return 'Invalid JSON data';
    }
  }, [data]);

  // Detect dark mode for JsonViewer
  React.useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  const handleCopy = () => {
    if (!jsonString) return;
    
    try {
      navigator.clipboard.writeText(jsonString).then(() => {
        setCopied(true);
        toast.success('JSON copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy: ', err);
        fallbackCopyTextToClipboard(jsonString);
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      fallbackCopyTextToClipboard(jsonString);
    }
  };

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        toast.success('JSON copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
  };

  const handleEdit = () => {
    setEditedJsonString(jsonString);
    setParseError('');
    setIsEditing(true);
  };



  const handleCancel = () => {
    setEditedJsonString('');
    setParseError('');
    setIsEditing(false);
  };

  const handleDownload = () => {
    try {
      // Use edited version if in edit mode, otherwise use original
      const contentToDownload = isEditing ? editedJsonString : jsonString;
      const blob = new Blob([contentToDownload], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('JSON file downloaded');
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  // Initialize edit mode with the current JSON string when editable and starting in edit mode
  React.useEffect(() => {
    if (editable && isEditing && !editedJsonString) {
      setEditedJsonString(jsonString);
    }
  }, [editable, isEditing, jsonString, editedJsonString]);

  // Auto-start in edit mode only if editable is true AND there's actual JSON content
  React.useEffect(() => {
    if (editable && data && jsonString && jsonString !== 'Invalid JSON data' && !isEditing) {
      setIsEditing(true);
    }
  }, [editable, data, jsonString, isEditing]);

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  const getBackgroundStyles = () => {
    switch (theme) {
      case 'aurora':
        return 'bg-gradient-to-br from-aurora-blue/5 via-aurora-purple/5 to-aurora-pink/5 dark:from-aurora-blue/10 dark:via-aurora-purple/10 dark:to-aurora-pink/10';
      default:
        return 'bg-muted/50 dark:bg-slate-950';
    }
  };

  const getButtonStyles = () => {
    switch (theme) {
      case 'aurora':
        return 'bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20';
      default:
        return 'bg-muted hover:bg-muted/80 dark:bg-slate-800 dark:hover:bg-slate-700';
    }
  };

  if (data === null || data === undefined) {
    return (
      <div className={cn('rounded-lg border border-border p-4', getBackgroundStyles(), className)}>
        <span className="text-muted-foreground italic">No data to display</span>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg overflow-hidden border border-border', className)}>
      <div className="px-4 py-2 bg-muted/30 dark:bg-slate-900 border-b border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-mono">JSON</span>
        <div className="flex items-center gap-2">
          {editable && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEdit}
              className={cn('h-6 px-2', getButtonStyles())}
            >
              <Edit3 className="h-3 w-3" />
            </Button>
          )}
          {editable && isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className={cn('h-6 px-2', getButtonStyles())}
              title="Exit edit mode"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFullscreen}
            className={cn('h-6 px-2', getButtonStyles())}
            title="Open in fullscreen"
          >
            <Maximize className="h-3 w-3" />
          </Button>
          {showDownload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className={cn('h-6 px-2', getButtonStyles())}
            >
              <Download className="h-3 w-3" />
            </Button>
          )}
          {showCopy && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className={cn('h-6 px-2', getButtonStyles())}
            >
              {copied ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
      </div>
      {parseError && (
        <div className="px-4 py-2 bg-destructive/10 border-b border-destructive/20">
          <span className="text-xs text-destructive">{parseError}</span>
        </div>
      )}
      <div className={cn('relative group', getBackgroundStyles())}>
        {isEditing ? (
          <div className="w-full p-4">
            <JsonEditor
              value={editedJsonString}
              onChange={setEditedJsonString}
              theme={theme}
              className="w-full"
              placeholder="Enter valid JSON..."
              maxHeight={maxHeight}
            />
          </div>
        ) : (
          <ScrollArea className="w-full" style={{ maxHeight }}>
            <div ref={containerRef} className="p-4 font-mono text-sm">
              <JsonNode 
                data={data} 
                level={0} 
                theme={theme}
                defaultExpanded={defaultExpanded}
              />
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Fullscreen Modal */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>JSON Editor</span>
              {fileName && <span className="text-sm text-muted-foreground">- {fileName}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {isEditing ? (
              <CodeEditor
                value={editedJsonString}
                onChange={setEditedJsonString}
                language="json"
                theme={isDarkMode ? 'dark' : 'light'}
                className="w-full h-full"
                placeholder="Enter valid JSON..."
                height="calc(95vh - 120px)"
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  dropCursor: false,
                  allowMultipleSelections: false,
                  searchKeymap: true,
                }}
                style={{
                  fontSize: '14px',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                }}
              />
            ) : (
              <ScrollArea className="w-full h-full" style={{ maxHeight: 'calc(95vh - 120px)' }}>
                <div className="p-4 font-mono text-sm">
                  <JsonNode 
                    data={data} 
                    level={0} 
                    theme={theme}
                    defaultExpanded={defaultExpanded}
                  />
                </div>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JsonNode({ 
  data, 
  keyName, 
  level = 0, 
  isLast = true,
  theme = 'default',
  defaultExpanded = false
}: JsonNodeProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || level < 2);
  
  const indent = '  '.repeat(level);
  
  const getValueColor = (value: any, theme: string) => {
    if (value === null) return 'text-muted-foreground';
    if (typeof value === 'string') return theme === 'aurora' ? 'text-aurora-green' : 'text-green-600 dark:text-green-400';
    if (typeof value === 'number') return theme === 'aurora' ? 'text-aurora-blue' : 'text-blue-600 dark:text-blue-400';
    if (typeof value === 'boolean') return theme === 'aurora' ? 'text-aurora-orange' : 'text-orange-600 dark:text-orange-400';
    return 'text-foreground';
  };

  const getKeyColor = (theme: string) => {
    return theme === 'aurora' ? 'text-aurora-purple' : 'text-purple-600 dark:text-purple-400';
  };

  if (data === null) {
    return (
      <div className="flex">
        <span className="mr-2">{indent}</span>
        {keyName && (
          <>
            <span className={getKeyColor(theme)}>"{keyName}"</span>
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <span className="text-muted-foreground italic">null</span>
        {!isLast && <span className="text-muted-foreground">,</span>}
      </div>
    );
  }

  if (typeof data !== 'object') {
    return (
      <div className="flex">
        <span className="mr-2">{indent}</span>
        {keyName && (
          <>
            <span className={getKeyColor(theme)}>"{keyName}"</span>
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <span className={getValueColor(data, theme)}>
          {typeof data === 'string' ? `"${data}"` : String(data)}
        </span>
        {!isLast && <span className="text-muted-foreground">,</span>}
      </div>
    );
  }

  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((item, index) => [index, item]) : Object.entries(data);
  const isEmpty = entries.length === 0;

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  if (isEmpty) {
    return (
      <div className="flex">
        <span className="mr-2">{indent}</span>
        {keyName && (
          <>
            <span className={getKeyColor(theme)}>"{keyName}"</span>
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <span className="text-muted-foreground">
          {isArray ? '[]' : '{}'}
        </span>
        {!isLast && <span className="text-muted-foreground">,</span>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center">
        <span className="mr-2">{indent}</span>
        <button
          onClick={toggleExpanded}
          className="mr-1 p-0.5 hover:bg-muted/50 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </button>
        {keyName && (
          <>
            <span className={getKeyColor(theme)}>"{keyName}"</span>
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <span className="text-muted-foreground">
          {isArray ? '[' : '{'}
        </span>
        {!isExpanded && (
          <>
            <span className="text-muted-foreground mx-1">
              {entries.length} {isArray ? 'items' : 'keys'}
            </span>
            <span className="text-muted-foreground">
              {isArray ? ']' : '}'}
            </span>
          </>
        )}
      </div>
      
      {isExpanded && (
        <>
          {entries.map(([key, value], index) => (
            <JsonNode
              key={key}
              data={value}
              keyName={isArray ? undefined : String(key)}
              level={level + 1}
              isLast={index === entries.length - 1}
              theme={theme}
              defaultExpanded={defaultExpanded}
            />
          ))}
          <div className="flex">
            <span className="mr-2">{indent}</span>
            <span className="text-muted-foreground">
              {isArray ? ']' : '}'}
            </span>
            {!isLast && <span className="text-muted-foreground">,</span>}
          </div>
        </>
      )}
    </div>
  );
}